import apiClient from './api';

/**
 * Authentication Service
 * Handles all authentication-related API operations
 */

class AuthService {
  /**
   * Login user
   * @param {Object} credentials - Email and password
   * @returns {Promise} Login response
   */
  async login(credentials) {
    try {
      const response = await apiClient.post('/api/user/login', credentials);
      
      // Store token and user data
      if (response.data.data?.token) {
        localStorage.setItem('accessToken', response.data.data.token);
        // Store user data for display in header
        if (response.data.data?.user) {
          localStorage.setItem('userData', JSON.stringify(response.data.data.user));
        }
      }
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Login failed',
        errors: error.errors,
      };
    }
  }

  /**
   * Login admin
   * @param {Object} credentials - Email and password
   * @returns {Promise} Admin login response
   */
  async loginAdmin(credentials) {
    try {
      const response = await apiClient.post('/api/admin/login', credentials);
      
      // Store admin token and admin data
      if (response.data.data?.token) {
        localStorage.setItem('adminAccessToken', response.data.data.token);
        // Store admin data for display in header
        if (response.data.data?.user) {
          localStorage.setItem('adminData', JSON.stringify(response.data.data.user));
        }
      }
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Admin login failed',
        errors: error.errors,
      };
    }
  }

  /**
   * Register user
   * @param {Object} userData - User registration data
   * @returns {Promise} Registration response
   */
  async register(userData) {
    try {
      const response = await apiClient.post('/api/user/register', userData);
      
      // Store token
      if (response.data.data?.token) {
        localStorage.setItem('accessToken', response.data.data.token);
      }
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Registration failed',
        errors: error.errors,
      };
    }
  }

  /**
   * Logout user
   * @returns {Promise} Logout response
   */
  async logout() {
    try {
      await apiClient.post('/api/user/logout');
      
      // Clear token and user data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userData');
      sessionStorage.removeItem('selectedPatient');
      
      return {
        success: true,
        message: 'Logged out successfully',
      };
    } catch (error) {
      // Clear token even if API call fails
      localStorage.removeItem('accessToken');
      sessionStorage.removeItem('selectedPatient');
      
      throw {
        success: false,
        message: error.message || 'Logout failed',
      };
    }
  }

  /**
   * Logout admin
   * @returns {Promise} Admin logout response
   */
  async logoutAdmin() {
    try {
      await apiClient.post('/api/admin/logout');
      
      // Clear admin token and admin data
      localStorage.removeItem('adminAccessToken');
      localStorage.removeItem('adminData');
      sessionStorage.removeItem('selectedPatient');
      
      return {
        success: true,
        message: 'Admin logged out successfully',
      };
    } catch (error) {
      // Clear token even if API call fails
      localStorage.removeItem('adminAccessToken');
      sessionStorage.removeItem('selectedPatient');
      
      throw {
        success: false,
        message: error.message || 'Admin logout failed',
      };
    }
  }

  /**
   * Get current user
   * @returns {Object|null} Current user or null
   */
  getCurrentUser() {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    
    // You can decode JWT here if needed
    return { token };
  }

  /**
   * Get current admin
   * @returns {Object|null} Current admin or null
   */
  getCurrentAdmin() {
    const token = localStorage.getItem('adminAccessToken');
    if (!token) return null;
    
    // You can decode JWT here if needed
    return { token };
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    return !!localStorage.getItem('accessToken');
  }

  /**
   * Check if admin is authenticated
   * @returns {boolean} Admin authentication status
   */
  isAdminAuthenticated() {
    return !!localStorage.getItem('adminAccessToken');
  }
}

export default new AuthService();

